import { useEffect, useState } from "react";
import { Alert, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FaClipboardList, FaTools } from "react-icons/fa";
import RulesHelp from "../components/shared/RulesHelp.jsx";
import { FilterSelect, ClaimLink } from "../components/reports/reportShared.jsx";
import { getJobScheduleReport, getCurrentUser, listUsers } from "../services/apiClient.js";
import { formatDate } from "../utils/date.js";

// Job Schedule (legacy JobScheduleReport.aspx): six live buckets of jobs for a
// PCO — In Progress, On Hold, To Organise, Problem, Important, Callout In
// Progress. Defaults to YOUR jobs, as 1.0. Deliberate 2.0 differences: the
// hidden never-working date filters aren't ported, and rows click through to
// the claim instead of 1.0's inline editing (one home per fact — dates/status
// live on Details, quick facts in the ⓘ glance).
//
// Display (Robert 2026-07-17): each bucket keeps 1.0's colour orientation via
// bootstrap table variants (no custom CSS); Contractors + Job Summary WRAP
// instead of truncating — the compact columns (dates/status) make the room;
// per-row Progress + Orders jump icons as 1.0.

const BUCKET_VARIANTS = {
  in_progress: "success",
  on_hold: "warning",
  to_organise: "info",
  problem: "danger",
  important: "primary",
  callout_in_progress: "secondary",
};

function Bucket({ bucket }) {
  const navigate = useNavigate();
  const variant = BUCKET_VARIANTS[bucket.key] || "light";
  return (
    <div className="mb-4">
      <h6 className={`text-uppercase fw-semibold small text-${variant}`} style={{ letterSpacing: "0.06em" }}>
        {bucket.label} <span className="fw-normal text-muted">({bucket.rows.length}{bucket.capped ? "+" : ""})</span>
      </h6>
      {bucket.rows.length === 0 ? (
        <p className="text-muted small mb-0">None.</p>
      ) : (
        <Table striped bordered hover responsive size="sm" className="align-middle mb-1">
          <thead className={`table-${variant}`}>
            <tr>
              <th>Job #</th>
              <th>Claim #</th>
              <th style={{ width: 180 }}>Address</th>
              <th className="text-nowrap">Start</th>
              <th className="text-nowrap">End</th>
              <th>Status</th>
              <th style={{ minWidth: 220 }}>Contractors</th>
              <th style={{ minWidth: 280 }}>Job Summary</th>
              <th>Lockbox</th>
              {/* Jump icons are navigation, not information: they are <svg>, not
                  .btn, so nothing else stops them printing as two meaningless
                  glyphs on every row. */}
              <th className="no-print" />
            </tr>
          </thead>
          <tbody>
            {bucket.rows.map((r) => (
              <tr key={r.claim_id}>
                <td><ClaimLink claimId={r.claim_id} claimNumber={r.claim_number} /></td>
                <td className="text-nowrap">{r.insurance_claim_number || "-"}</td>
                <td className="small">{r.street_address || "-"}</td>
                <td className="text-nowrap">{r.actual_start_date ? formatDate(r.actual_start_date) : "-"}</td>
                <td className="text-nowrap">{r.actual_end_date ? formatDate(r.actual_end_date) : "-"}</td>
                <td className="text-nowrap">
                  {r.accepted_status === "On Hold" ? (
                    <span className="text-danger">{r.on_hold_reason || "On Hold"}</span>
                  ) : (
                    r.accepted_status || "-"
                  )}
                </td>
                <td className="small">{r.contractors || "-"}</td>
                <td className="small">{r.job_summary || "-"}</td>
                <td className="text-nowrap small">{r.lockbox_location || "-"}</td>
                <td className="text-nowrap text-center no-print">
                  <FaClipboardList
                    role="button" className="text-success me-2" data-hint="Progress" aria-label="Progress"
                    onClick={() => navigate(`/claims/${r.claim_number}?tab=progress`)}
                  />
                  <FaTools
                    role="button" className="text-body" data-hint="Orders" aria-label="Orders"
                    onClick={() => navigate(`/claims/${r.claim_number}?tab=orders`)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {bucket.capped ? <div className="small text-warning">Showing the first {bucket.rows.length} — narrow with the PCO filter.</div> : null}
    </div>
  );
}

function JobScheduleReportPage() {
  const [users, setUsers] = useState([]);
  const [pco, setPco] = useState(null); // null until the default (me) resolves
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");

  // 1.0 pre-selects the current user — but ONLY when they are actually a project
  // co-ordinator (Robert 2026-08-01). The filter lists PCOs, so selecting anyone
  // else put an id in the box with no matching option: the control fell back to
  // showing "All" while the report was filtered to a person who owns no jobs, so
  // an accounts or admin user opened the page to six empty buckets and nothing to
  // explain them. Not a PCO = no filter, which is the honest default.
  useEffect(() => {
    let cancelled = false;
    Promise.all([listUsers({ pco: true, activeOnly: true }), getCurrentUser().catch(() => null)])
      .then(([list, me]) => {
        if (cancelled) return;
        setUsers(list);
        const isPco = me && list.some((u) => String(u.id) === String(me.id));
        setPco(isPco ? String(me.id) : "");
      })
      .catch(() => {
        if (!cancelled) setPco("");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (pco === null) return;
    setError("");
    setReport(null);
    getJobScheduleReport({ project_coordinator_id: pco })
      .then(setReport)
      .catch((err) => setError(err.response?.data?.error || "Unable to load the report."));
  }, [pco]);

  return (
    <section className="page-panel">
      <div className="page-heading d-flex flex-wrap justify-content-between align-items-end column-gap-4">
        <h2>Job Schedule <RulesHelp topic="jobSchedule" /></h2>
        <div className="no-print"><FilterSelect label="Project Co-Ordinator" value={pco || ""} onChange={setPco} options={users} labelField="full_name" width={220} /></div>
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {!report ? (
        <p className="text-muted">Loading…</p>
      ) : (
        report.buckets.map((b) => <Bucket key={b.key} bucket={b} />)
      )}
    </section>
  );
}

export default JobScheduleReportPage;
